<template>
    <div
        :class="containerClass"
    >
        <div
            v-if="!userCanSeeVideo"
            class="video-player__empty"
        >
            <div class="video-player__empty-message">
                {{ $capitalize($t('login to watch video').toString()) }}
            </div>
            <CustomButton
                theme="primary"
                size="md"
                @click="$emit('requestLogin')"
                class="btn--video btn--video-login"
            >
                {{ $capitalize($t('login').toString()) }}
            </CustomButton>
        </div>
        <div
            v-if="isDetached"
            class="video-player__empty"
        >
            <div class="video-player__empty-message">
                {{ $capitalize($t('Video is pinned. You can see details for other '
                    + 'events and still watch your pinned video.').toString()) }}
            </div>
        </div>
        <div
            v-if="isUnavailable"
            class="video-player__empty"
        >
            <div class="video-player__empty-message">
                {{ $capitalize($t('video not available').toString()) }}
            </div>
        </div>
        <div
            v-if="isLoading"
            class="video-player__empty"
        >
            <div class="video-player__empty-message">
                <SpinnerBars
                    class="spinner-bars--video-player"
                />
            </div>
        </div>
        <div
            v-else-if="isClosed"
            class="video-player__empty"
        >
            <CustomButton
                theme="primary"
                size="md"
                @click="$emit('resumePlayback')"
                class="btn--video"
            >
                {{ $capitalize($t('resume video').toString()) }}
            </CustomButton>
        </div>
        <div
            v-if="isNotYetStarted"
            class="video-player__empty"
        >
            <CustomButton
                theme="primary"
                size="md"
                @click="$emit('startPlaying')"
                class="btn--video"
            >
                {{ $capitalize($t('play video').toString()) }}
            </CustomButton>
        </div>
        <YoutubePlayer
            v-if="isYoutube && isPlaying"
            :videoId="videoId"
            :rerenderingIndicator="position"
        />
        <TwitchPlayer
            v-else-if="isTwitch && isPlaying"
            :channel="videoId"
            :rerenderingIndicator="position"
        />
        <PerformPlayer
            v-else-if="isPerform && isPlaying"
            :uuid="uuid"
            :streamId="streamId"
            :username="username"
            :rerenderingIndicator="position"
        />
        <BetGeniusPlayer
            v-else-if="isBetGenius && isPlaying"
            :videoId="videoId"
            :sourceId="sourceId"
            :rerenderingIndicator="position"
        />
        <BeterPlayer
            v-else-if="isBeter && isPlaying"
            :streamId="streamId"
        />
        <VideoPlayer
            v-else-if="isPlaying"
            :streamUrl="streamUrl"
            :videoType="videoType"
            :rerenderingIndicator="position"
            :isSoundOn="isSoundOn"
            @toggleSound="$emit('toggleSound')"
            @error="setUnavailable"
        />
    </div>
</template>

<script>
    import {
        mapActions,
        mapGetters,
    } from 'vuex';

    import { VideoType } from '@core/services/media/enums';
    import { StreamState } from '@store/modules/ui/sportOffer/sportPage/enums';

    export default {
        name: 'VideoContainer',
        emits: [
            'videoPinClick',
            'videoCloseClick',
            'videoUnavailable',
            'requestLogin',
            'resumePlayback',
            'startPlaying',
            'toggleSound',
        ],
        inheritAttrs: false,
        props: {
            streamUrl: {
                type: String,
                default: '',
            },
            videoId: {
                type: String,
                required: false,
                default: '',
            },
            streamId: {
                type: String,
                required: false,
                default: '',
            },
            sourceId: {
                type: String,
                required: false,
                default: '',
            },
            uuid: {
                type: String,
                required: false,
                default: '',
            },
            token: {
                type: String,
                required: false,
                default: '',
            },
            videoType: {
                type: String,
                default: 'video/mp4',
            },
            state: {
                type: String,
                required: false,
                default: StreamState.NotAvailable,
            },
            isPinned: {
                type: Boolean,
                required: true,
            },
            position: {
                type: [
                    Number,
                    String,
                ],
                default: 1,
            },
            isDetached: {
                type: Boolean,
                default: false,
            },
            isSignedIn: {
                type: Boolean,
                default: true,
            },
            isSoundOn: {
                type: Boolean,
                default: true,
            },
        },
        computed: {
            ...mapGetters('data/authentication', [
                'user',
            ]),
            ...mapGetters('ui/sportOffer', [
                'expandedEventData',
                'selectedStreamSection',
            ]),
            isLoading() {
                return this.userCanSeeVideo && !this.isDetached && this.state === StreamState.Loading;
            },
            isUnavailable() {
                return this.userCanSeeVideo && !this.isDetached && this.state === StreamState.NotAvailable;
            },
            isPlaying() {
                return this.userCanSeeVideo && !this.isDetached && this.state === StreamState.Playing;
            },
            isClosed() {
                return this.userCanSeeVideo && !this.isDetached && this.state === StreamState.Closed;
            },
            isNotYetStarted() {
                return this.userCanSeeVideo && !this.isDetached && this.state === StreamState.NotStarted;
            },
            username() {
                if (!this.user) {
                    return null;
                }
                return this.user.username;
            },
            userCanSeeVideo() {
                if (this.isYoutube || this.isTwitch) {
                    return true;
                }

                return this.isSignedIn;
            },
            icon() {
                return this.isPinned ? 'icon-pin_45_filled_right' : 'icon-pin_45_right';
            },
            containerClass() {
                const classNames = ['video-player-container'];
                if (this.isDetached) {
                    classNames.push('video-player-container-detached');
                } else if (this.isUnavailable || !this.userCanSeeVideo) {
                    classNames.push('video-player-container-unavaliable');
                } else if (this.isPinned) {
                    classNames.push('video-player-container-docked');
                } else {
                    classNames.push('video-player-container-floating');
                }
                return classNames;
            },
            isYoutube() {
                return VideoType.youtube === this.videoType;
            },
            isBetGenius() {
                return VideoType.betGenius === this.videoType;
            },
            isTwitch() {
                return VideoType.twitch === this.videoType;
            },
            isPerform() {
                return VideoType.perform === this.videoType;
            },
            isBeter() {
                return VideoType.beter === this.videoType;
            },
        },
        watch: {
            user(newValue) {
                if (!newValue) {
                    if (this.videoType === VideoType.youtube || this.videoType === VideoType.twitch) {
                        return;
                    }
                    this.clearDockedStream();
                }

                if (newValue) {
                    this.prepareStream({ match: this.expandedEventData, streamSection: this.selectedStreamSection });
                }
            },
        },
        methods: {
            ...mapActions('ui/sportOffer', [
                'clearDockedStream',
                'prepareStream',
            ]),
            unpin() {
                this.$emit('videoPinClick');
            },
            close() {
                this.$emit('videoCloseClick');
            },
            setUnavailable() {
                this.$emit('videoUnavailable');
            },
        },
        beforeDestroy() {
            this.clearDockedStream();
        },
        destroyed() {
            if (this.betGeniusUnsubscribe) {
                this.betGeniusUnsubscribe();
            }
        },
    };
</script>
